/**
 * File type icon utilities for attachment display.
 *
 * Maps MIME types to appropriate Lucide icons and human-readable labels.
 */

import type { LucideIcon } from '@/components/icons'
import {
  FileText,
  FileSpreadsheet,
  File,
  Image as ImageIcon,
  FileType,
  FileCode,
  Presentation,
  Video,
} from '@/components/icons'

/**
 * Get the appropriate icon component for a file's MIME type.
 */
export function getFileIcon(mimeType: string): LucideIcon {
  // Images
  if (mimeType.startsWith('image/')) {
    return ImageIcon
  }

  // Videos
  if (mimeType.startsWith('video/')) {
    return Video
  }

  // PDF
  if (mimeType === 'application/pdf') {
    return FileText
  }

  // Word documents
  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/msword'
  ) {
    return FileText
  }

  // PowerPoint presentations
  if (
    mimeType ===
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ) {
    return Presentation
  }

  // Excel spreadsheets
  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel'
  ) {
    return FileSpreadsheet
  }

  // CSV
  if (mimeType === 'text/csv') {
    return FileSpreadsheet
  }

  // JSON
  if (mimeType === 'application/json') {
    return FileText
  }

  // HTML
  if (mimeType === 'text/html') {
    return FileCode
  }

  // Markdown
  if (mimeType === 'text/markdown') {
    return FileType
  }

  // Plain text
  if (mimeType === 'text/plain') {
    return FileText
  }

  // Default fallback
  return File
}

/**
 * Get a human-readable label for a file's MIME type.
 */
export function getFileTypeLabel(mimeType: string): string {
  // Images
  if (mimeType === 'image/jpeg') return 'JPEG Image'
  if (mimeType === 'image/png') return 'PNG Image'
  if (mimeType === 'image/webp') return 'WebP Image'
  if (mimeType.startsWith('image/')) return 'Image'

  // Videos
  if (mimeType === 'video/mp4') return 'MP4 Video'
  if (mimeType === 'video/webm') return 'WebM Video'
  if (mimeType === 'video/quicktime') return 'QuickTime Video'
  if (mimeType.startsWith('video/')) return 'Video'

  // PDF
  if (mimeType === 'application/pdf') return 'PDF'

  // Word documents
  if (
    mimeType ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'Word Document'
  }

  // PowerPoint presentations
  if (
    mimeType ===
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ) {
    return 'PowerPoint Presentation'
  }

  // Excel spreadsheets
  if (
    mimeType ===
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return 'Excel Spreadsheet'
  }

  // CSV
  if (mimeType === 'text/csv') return 'CSV'

  // JSON
  if (mimeType === 'application/json') return 'JSON'

  // HTML
  if (mimeType === 'text/html') return 'HTML Document'

  // Markdown
  if (mimeType === 'text/markdown') return 'Markdown'

  // Plain text
  if (mimeType === 'text/plain') return 'Text File'

  // Default
  return 'File'
}

/**
 * Get a short file type abbreviation for badges/chips.
 */
export function getFileTypeAbbreviation(mimeType: string): string {
  if (mimeType === 'image/jpeg') return 'JPG'
  if (mimeType === 'image/png') return 'PNG'
  if (mimeType === 'image/webp') return 'WEBP'
  if (mimeType.startsWith('image/')) return 'IMG'
  if (mimeType === 'video/mp4') return 'MP4'
  if (mimeType === 'video/webm') return 'WEBM'
  if (mimeType === 'video/quicktime') return 'MOV'
  if (mimeType.startsWith('video/')) return 'VIDEO'
  if (mimeType === 'application/pdf') return 'PDF'
  if (
    mimeType ===
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return 'DOCX'
  }
  if (
    mimeType ===
    'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ) {
    return 'PPTX'
  }
  if (
    mimeType ===
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return 'XLSX'
  }
  if (mimeType === 'text/csv') return 'CSV'
  if (mimeType === 'application/json') return 'JSON'
  if (mimeType === 'text/html') return 'HTML'
  if (mimeType === 'text/markdown') return 'MD'
  if (mimeType === 'text/plain') return 'TXT'
  return 'FILE'
}

/**
 * Check if a MIME type represents an image that can be previewed.
 */
export function isPreviewableImage(mimeType: string): boolean {
  return mimeType.startsWith('image/')
}

/**
 * Check if a MIME type represents a video that can be played inline.
 */
export function isPreviewableVideo(mimeType: string): boolean {
  return (
    mimeType === 'video/mp4' ||
    mimeType === 'video/webm' ||
    mimeType === 'video/quicktime'
  )
}

/**
 * Check if a file type should be downloaded rather than opened inline.
 */
export function shouldDownload(mimeType: string): boolean {
  // Most document types should be downloaded
  return (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType ===
      'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    mimeType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'text/csv'
  )
}

/**
 * Check if a MIME type represents a document that can be previewed in-app.
 * This includes PDFs, Word docs, Excel spreadsheets, text files, etc.
 */
export function isPreviewableDocument(mimeType: string): boolean {
  return (
    // PDF
    mimeType === 'application/pdf' ||
    // Word documents
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/msword' ||
    // Excel spreadsheets
    mimeType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel' ||
    // Text files
    mimeType === 'text/plain' ||
    mimeType === 'text/csv' ||
    mimeType === 'text/markdown' ||
    mimeType === 'text/html' ||
    // JSON
    mimeType === 'application/json'
  )
}

/**
 * Check if a MIME type supports server-side thumbnail generation.
 * Returns the type of thumbnail support: 'full', 'icon-only', or 'none'
 */
export function getThumbnailSupport(
  mimeType: string,
): 'full' | 'icon-only' | 'none' {
  // Images already have their own preview
  if (mimeType.startsWith('image/')) {
    return 'none' // Use the image itself, not a thumbnail
  }

  // PDF supports full thumbnail generation (first page render)
  if (mimeType === 'application/pdf') {
    return 'full'
  }

  // DOCX, PPTX, and XLSX get styled icon-based thumbnails
  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/msword' ||
    mimeType ===
      'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    mimeType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel'
  ) {
    return 'icon-only'
  }

  // Text files get icon-only thumbnails
  if (
    mimeType === 'text/plain' ||
    mimeType === 'text/csv' ||
    mimeType === 'text/markdown' ||
    mimeType === 'text/html'
  ) {
    return 'icon-only'
  }

  // JSON gets icon-only thumbnail
  if (mimeType === 'application/json') {
    return 'icon-only'
  }

  return 'none'
}

/**
 * Get the viewer type for a given MIME type.
 * Used to determine which preview component to render.
 */
export function getViewerType(
  mimeType: string,
):
  | 'image'
  | 'pdf'
  | 'docx'
  | 'xlsx'
  | 'text'
  | 'csv'
  | 'json'
  | 'markdown'
  | 'video'
  | 'unsupported' {
  if (mimeType.startsWith('image/')) {
    return 'image'
  }

  if (mimeType.startsWith('video/')) {
    return 'video'
  }

  if (mimeType === 'application/pdf') {
    return 'pdf'
  }

  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/msword'
  ) {
    return 'docx'
  }

  if (
    mimeType ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel'
  ) {
    return 'xlsx'
  }

  if (mimeType === 'text/csv') {
    return 'csv'
  }

  if (mimeType === 'application/json') {
    return 'json'
  }

  if (mimeType === 'text/markdown') {
    return 'markdown'
  }

  // HTML displayed as plain text for XSS safety
  if (mimeType === 'text/plain' || mimeType === 'text/html') {
    return 'text'
  }

  return 'unsupported'
}
